import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, ExternalLink, Github, RefreshCw } from 'lucide-react'
import {
  getSmeFeedbackScreenshotUrl,
  listSmeFeedback,
  promoteSmeFeedback,
  updateSmeFeedbackStatus,
  type SmeFeedbackItem,
  type SmeFeedbackStatus,
} from '../../lib/services/smeFeedback'

const STATUS_OPTIONS: SmeFeedbackStatus[] = ['new', 'triaged', 'accepted', 'fixed', 'verified', 'closed']

function screenshotMetadata(feedback: SmeFeedbackItem): Record<string, unknown> | null {
  const screenshot = feedback.page_metadata.screenshot
  if (typeof screenshot !== 'object' || screenshot === null || Array.isArray(screenshot)) return null
  return screenshot as Record<string, unknown>
}

export function SmeFeedbackBacklogPage() {
  const queryClient = useQueryClient()
  const feedbackQuery = useQuery({
    queryKey: ['sme-feedback-backlog'],
    queryFn: () => listSmeFeedback(),
    staleTime: 30_000,
  })

  const statusMutation = useMutation({
    mutationFn: ({ feedbackId, status }: { feedbackId: string; status: SmeFeedbackStatus }) =>
      updateSmeFeedbackStatus(feedbackId, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sme-feedback-backlog'] }),
  })

  const promoteMutation = useMutation({
    mutationFn: (feedbackId: string) => promoteSmeFeedback(feedbackId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sme-feedback-backlog'] }),
  })

  const screenshotMutation = useMutation({
    mutationFn: (feedbackId: string) => getSmeFeedbackScreenshotUrl(feedbackId),
    onSuccess: (signedUrl) => {
      window.open(signedUrl, '_blank', 'noopener,noreferrer')
    },
  })

  const feedback = feedbackQuery.data ?? []

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-wider text-rose-600">SME Bug Backlog</p>
          <h1 className="font-brand text-3xl font-extrabold text-slate-950">Review Feedback</h1>
          <p className="mt-1 max-w-2xl text-sm font-semibold text-slate-500">
            Triage review notes, pinned page issues, and screenshot metadata from SME workflow reviews.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void feedbackQuery.refetch()}
          className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700"
        >
          <RefreshCw size={15} className={feedbackQuery.isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {feedbackQuery.isLoading && <p className="text-sm font-semibold text-slate-500">Loading feedback...</p>}
      {feedbackQuery.error && <p className="text-sm font-bold text-red-600">{feedbackQuery.error.message}</p>}
      {!feedbackQuery.isLoading && feedback.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm font-semibold text-slate-500">
          No SME feedback has been submitted yet.
        </div>
      )}

      <div className="grid gap-4">
        {feedback.map((item) => {
          const screenshot = screenshotMetadata(item)
          const github = item.page_metadata.github
          const githubUrl = typeof github === 'object' && github !== null && !Array.isArray(github)
            ? (github as Record<string, unknown>).issue_url
            : null
          const isPromoting = promoteMutation.isPending && promoteMutation.variables === item.feedback_id

          return (
            <article key={item.feedback_id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-rose-50 px-2 py-1 text-[11px] font-extrabold uppercase text-rose-700">
                      {item.category.replace(/_/g, ' ')}
                    </span>
                    <span className="rounded bg-slate-100 px-2 py-1 text-[11px] font-extrabold uppercase text-slate-600">
                      {item.severity}
                    </span>
                    <span className="rounded bg-emerald-50 px-2 py-1 text-[11px] font-extrabold uppercase text-emerald-700">
                      {item.status}
                    </span>
                  </div>
                  <h2 className="mt-2 text-base font-extrabold text-slate-900">{item.page_path}</h2>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{item.note}</p>
                </div>
                <div className="flex shrink-0 flex-col gap-2 md:w-44">
                  <select
                    value={item.status}
                    onChange={(event) => statusMutation.mutate({
                      feedbackId: item.feedback_id,
                      status: event.target.value as SmeFeedbackStatus,
                    })}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold"
                  >
                    {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => promoteMutation.mutate(item.feedback_id)}
                    disabled={isPromoting || typeof githubUrl === 'string'}
                    className="flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-extrabold text-white disabled:opacity-60"
                  >
                    <Github size={14} />
                    {githubUrl ? 'Promoted' : isPromoting ? 'Promoting...' : 'Promote'}
                  </button>
                  {typeof githubUrl === 'string' && (
                    <a
                      href={githubUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700"
                    >
                      <ExternalLink size={14} />
                      GitHub issue
                    </a>
                  )}
                </div>
              </div>

              {item.pins.length > 0 && (
                <div className="mt-4 rounded-lg bg-slate-50 p-3">
                  <p className="text-xs font-extrabold uppercase text-slate-500">Pins</p>
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    {item.pins.map((pin, index) => (
                      <div key={pin.id} className="rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-600">
                        <span className="font-bold text-slate-900">Pin {index + 1}</span>
                        <span> at {Math.round(pin.x * 100)}%, {Math.round(pin.y * 100)}%</span>
                        {pin.note && <p className="mt-1 text-slate-700">{pin.note}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {screenshot && (
                <div className="mt-4 rounded-lg bg-slate-50 p-3">
                  <p className="text-xs font-extrabold uppercase text-slate-500">Screenshot metadata</p>
                  <p className="mt-1 text-xs font-semibold text-slate-600">
                    Private screenshot captured
                    {typeof screenshot.size_bytes === 'number' ? ` (${Math.ceil(screenshot.size_bytes / 1024)} KB)` : ''}.
                  </p>
                  {item.screenshot_path && (
                    <button
                      type="button"
                      onClick={() => screenshotMutation.mutate(item.feedback_id)}
                      disabled={screenshotMutation.isPending && screenshotMutation.variables === item.feedback_id}
                      className="mt-2 flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 disabled:opacity-60"
                    >
                      <Camera size={14} />
                      {screenshotMutation.isPending && screenshotMutation.variables === item.feedback_id
                        ? 'Opening screenshot...'
                        : 'View screenshot'}
                    </button>
                  )}
                </div>
              )}
            </article>
          )
        })}
      </div>
    </div>
  )
}
